import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/atoms/card";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRef, useState } from "react";

interface DonutChartProps {
  data: {
    name: string;
    value: number;
    color: string;
  }[];
  title: string;
  totalLabel: string;
  total: number;
}

// Custom Tooltip para el donut chart usando el sistema de Recharts
const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any[];
}) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;
    return (
      <div
        className="bg-white rounded shadow px-3 py-2 border border-gray-200 text-xs z-50 pointer-events-none"
        style={{
          minWidth: 140,
          pointerEvents: "none",
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          ></span>
          <span className="font-semibold">{entry.name}</span>
        </div>
        <div>
          Valor:{" "}
          <span className="font-bold">{entry.value.toLocaleString()}</span>
        </div>
      </div>
    );
  }
  return null;
};

export default function DonutChart({
  data,
  title,
  totalLabel,
  total,
}: DonutChartProps) {
  const chartElementRef = useRef<HTMLDivElement>(null);
  const isSmallScreen = useMediaQuery("(max-width: 639px)");

  // Estado para el tooltip de la leyenda
  const [legendTooltip, setLegendTooltip] = useState<{
    visible: boolean;
    entry?: { name: string; value: number; color: string };
    position?: { x: number; y: number };
  }>({ visible: false });

  // Handler para mostrar el tooltip al lado del ref de la leyenda
  const handleLegendMouseEnter = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    entry: { name: string; value: number; color: string },
  ) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    // Calcula la posición del tooltip al lado derecho del ref
    setLegendTooltip({
      visible: true,
      entry,
      position: {
        x: rect.right + 8,
        y: rect.top + rect.height / 2,
      },
    });
  };

  const handleLegendMouseLeave = () => {
    setLegendTooltip({ visible: false });
  };

  return (
    <Card
      className="h-full pb-10 bg-white w-full min-w-0 "
      ref={chartElementRef}
      id="donut-chart-card"
      tabIndex={-1} // Prevent Card from being focusable
      style={{ outline: "none" }} // Remove any default outline
    >
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium truncate flex-1 mr-2">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          id="donut-chart"
          className="h-[300px] w-full flex flex-col items-center mb-8 sm:mb-4"
        >
          <div className="relative h-[260px] w-full">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={isSmallScreen ? 65 : 80}
                  outerRadius={isSmallScreen ? 90 : 110}
                  paddingAngle={0}
                  dataKey="value"
                  strokeWidth={2}
                  cornerRadius={10}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      className="hover:opacity-80 transition-opacity"
                      tabIndex={-1} // Prevent Cell from being focusable
                      style={{ outline: "none" }} // Remove outline if any
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                  isAnimationActive={false}
                  // No position: deja que el tooltip de Recharts se posicione automáticamente
                />
              </PieChart>
            </ResponsiveContainer>
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none select-none"
              style={{ zIndex: 1 }}
            >
              <div className="text-4xl font-bold text-gray-900">
                {total.toLocaleString()}
              </div>
              <div className="text-base text-gray-500 font-medium">
                {totalLabel}
              </div>
            </div>
          </div>
          {/* Custom Legend */}
          <div className="w-full mt-4 relative">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 max-w-full">
              {data.map((entry) => (
                <div
                  key={entry.name}
                  className="flex items-center gap-1.5 flex-shrink-0 min-w-0 relative"
                  onMouseEnter={(e) => handleLegendMouseEnter(e, entry)}
                  onMouseLeave={handleLegendMouseLeave}
                  onFocus={(e) => handleLegendMouseEnter(e as any, entry)}
                  onBlur={handleLegendMouseLeave}
                  tabIndex={0}
                  style={{ outline: "none", cursor: "pointer" }}
                  aria-label={`Mostrar información de ${entry.name}`}
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: entry.color }}
                  ></span>
                  <span className="text-xs sm:text-sm text-gray-600 truncate">
                    {entry.name}
                  </span>
                </div>
              ))}
            </div>
            {/* Tooltip para la leyenda */}
            {legendTooltip.visible &&
              legendTooltip.entry &&
              legendTooltip.position && (
                <div
                  className="fixed z-50 pointer-events-none"
                  style={{
                    top: legendTooltip.position.y,
                    left: legendTooltip.position.x,
                    transform: "translateY(-50%)",
                  }}
                >
                  <div
                    className="bg-white rounded shadow px-3 py-2 border border-gray-200 text-xs"
                    style={{ minWidth: 140 }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: legendTooltip.entry.color }}
                      ></span>
                      <span className="font-semibold">
                        {legendTooltip.entry.name}
                      </span>
                    </div>
                    <div>
                      Valor:{" "}
                      <span className="font-bold">
                        {legendTooltip.entry.value.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
